// One line of the transcript.
//
// ---------------------------------------------------------------------------
// A SYSTEM MESSAGE IS RENDERED FROM ITS METADATA AND NEVER FROM ITS BODY
// ---------------------------------------------------------------------------
//
// "System messages ('Seth joined') render from their metadata, not from stored prose, so
// they can be re-rendered later." `message.body` for a system message is prose somebody
// wrote once; `metadata.subject` is a roster reference the API validated against this
// plan's roster. Only the second is read here. That is why a person who changes how their
// name is written sees every "joined" line change with it, and why translating this
// interface later is a change to this file rather than a data migration.
//
// The body of a system message is in scope on the line below and is not read. That is
// deliberate: a version that read it would render whatever prose the server happened to
// store rather than the sentence this file composes.
//
// ---------------------------------------------------------------------------
// WHO SPOKE
// ---------------------------------------------------------------------------
//
// The speaker is `message.type` and, for a human, the name of `authorId` in the people
// list. There is no role anywhere: nobody is a principal here, nobody is an interested
// party, and there is no value in this component that could be turned into either — the
// person decoder drops the field before it becomes a value. See `api/contract.ts`.
//
// ---------------------------------------------------------------------------
// AND WHICH OF THEM IS YOU
// ---------------------------------------------------------------------------
//
// Every line in a family's transcript used to be a name, including yours, which left a
// reader working out which lines were theirs from the content — in the one interface where
// being wrong about who said something is worst. So a message whose author is the person
// reading says "You" and carries `message--mine`.
//
// THE ANSWER COMES OUT OF `people`, THE SAME ARRAY THE NAME COMES OUT OF, in the same
// lookup: `personFor` returns the roster entry and the caller asks it for a name or for
// `self`. Nothing is stored, nothing is compared against a session, and nothing here knows
// what an account is — so this component cannot be out of date about who is reading it,
// because the snapshot it is rendering carries the answer with the question.
//
// The word and the class are BOTH load-bearing. A class alone is a colour, and colour alone
// is not a distinction everyone can see — the same reason the facilitator's panel is a panel
// and not a tint. "You" is the plainest thing the interface can say, and it is the reader's
// own view: the record on the server is identical for everyone, and the only thing that
// differs is which line this reader is being shown as theirs.
//
// ---------------------------------------------------------------------------
// WHAT IS NOT HERE
// ---------------------------------------------------------------------------
//
// No edit, no delete, no reaction, no "seen by", no relative-time badge that ticks, and no
// READ RECEIPT — nothing here tells one participant what another has read. A message is a
// thing that was said. The only affordance on it is reading it, and the one thing this
// client tracks about that is self-facing: which threads YOU have unread, on the tab nav in
// `PlanScreen.tsx`, never who else has seen this line.

import {
  GRANT_PRINCIPAL_ACTION,
  INVITATION_ACTION,
  MOVE_ACTION,
  type Message,
  type Person,
} from '../api/contract.js';
import { dayAndTime, nameList } from '../format.js';

export type MessageRowProps = {
  message: Message;
  people: readonly Person[];
};

/** The facilitator's name in the interface. It speaks as a participant, not as a feature. */
export const FACILITATOR_NAME = 'Mor';

export function MessageRow({ message, people }: MessageRowProps): JSX.Element | null {
  if (message.type === 'system') {
    return (
      <li className="message message--system">
        <p className="message__systemText">{systemSentence(message, people)}</p>
      </li>
    );
  }

  const facilitator = message.type === 'facilitator';
  const author = facilitator ? null : personFor(message.authorId, people);
  const mine = author?.self === true;
  // Never the facilitator: Mor is named as itself and has no seat on the roster, so a mark
  // saying "a model wrote this" would be telling the reader something they can already see.
  const character = author?.character === true;
  const speaker = facilitator ? FACILITATOR_NAME : (author?.name ?? 'Someone');
  const addressed = facilitator ? addressee(message.metadata.addressed, people) : null;

  return (
    <li className={classFor(facilitator, mine)}>
      <p className="message__attribution">
        {character ? (
          // Which of the names answering you is a model. Only a demo has any, and the
          // reader was told so on the way in — this is what saves them holding the seat
          // list in their head while they read. `aria-label` because "AI" read aloud one
          // letter at a time in front of every character's name is noise, and the word it
          // stands for is what the mark means.
          <span className="message__character" aria-label="Played by AI">
            AI
          </span>
        ) : null}
        <span className="message__speaker">{mine ? 'You' : speaker}</span>
        {addressed === null ? null : (
          // What makes an ask legible to the room. The facilitator can address one person
          // in front of everyone, and everyone can see which of them was asked.
          <span className="message__addressed"> — to {addressed}</span>
        )}
        <time className="message__time" dateTime={message.createdAt}>
          {dayAndTime(message.createdAt)}
        </time>
      </p>
      <p className="message__body">{message.body}</p>
    </li>
  );
}

function classFor(facilitator: boolean, mine: boolean): string {
  if (facilitator) {
    return 'message message--facilitator';
  }
  return mine ? 'message message--person message--mine' : 'message message--person';
}

/**
 * The roster entry a message names, or null.
 *
 * One lookup, returning the whole person, so that "what are they called" and "is this me"
 * are answered from one row of one array rather than from two places that could disagree.
 */
function personFor(
  rosterEntryId: string | null,
  people: readonly Person[],
): Person | null {
  if (rosterEntryId === null) {
    return null;
  }
  return people.find((person) => person.id === rosterEntryId) ?? null;
}

/** Who the facilitator spoke to, in the second person when it is the person reading. */
function addressee(
  rosterEntryId: string | null,
  people: readonly Person[],
): string | null {
  const person = personFor(rosterEntryId, people);
  if (person === null) {
    return null;
  }
  return person.self ? 'you' : person.name;
}

/**
 * The four system occurrences the metadata can describe: somebody joining, an
 * invitation being approved, a confirmation being answered, or the terminal speaking
 * notice.
 *
 * `systemKind` is the whole of how this file tells them apart. Every roster reference the
 * first three carry — `subject`, `target`, `notWrittenTo` — is one the server has already
 * checked against this plan's roster, and one the roster does not contain is dropped
 * before it gets here; the fallbacks below are for that case, and say the true thing
 * without naming anybody. The speaking notice carries none of them: its sentence names
 * nobody and reports no outcome, so it needs nothing out of `metadata` at all.
 */
function systemSentence(message: Message, people: readonly Person[]): string {
  if (message.metadata.systemKind === 'speaking_notice') {
    // The terminal notice: the group loop's speaking fallback and the 1:1 loop's dropped
    // bundle both raise this same system entry, never a facilitator message — see
    // `docs/decisions.md`. Composed here, from the kind alone, and never from
    // `message.body`: the wording lives in exactly one place.
    return 'Something went wrong. Nothing anyone said has been lost.';
  }
  if (message.metadata.systemKind === 'confirmation_resolved') {
    const actor = personFor(message.metadata.subject, people);
    const who = actor?.self === true ? 'You' : (actor?.name ?? 'A principal');
    const verb = message.metadata.outcome === 'ratified' ? 'approved' : 'declined';
    const target = personFor(message.metadata.target, people)?.name ?? 'them';
    const said = (() => {
      switch (message.metadata.actionType) {
        case MOVE_ACTION:
          return `${who} ${verb} starting a group conversation.`;
        case INVITATION_ACTION:
          return `${who} ${verb} inviting ${target}.`;
        case GRANT_PRINCIPAL_ACTION:
          return `${who} ${verb} making ${target} a principal.`;
        default:
          return `${who} ${verb} the confirmation.`;
      }
    })();
    const excluded = notWrittenToSentence(message.metadata.notWrittenTo, people);
    return excluded === null ? said : `${said} ${excluded}`;
  }
  if (message.metadata.systemKind === 'invited') {
    // D105: posted into every conversation the invited person will be seated in once
    // they join, so nobody already in it is surprised later by who can read it.
    const approver = personFor(message.metadata.subject, people);
    const who = approver?.self === true ? 'You' : (approver?.name ?? 'Someone');
    const target = personFor(message.metadata.target, people)?.name ?? 'someone';
    return `${who} invited ${target} to join this conversation.`;
  }
  return joinedSentence(message.metadata.subject, people);
}

/**
 * Who a ratification did not write to (issue #155, D96), as the permanent line that
 * survives in the thread — the resolve response says the same thing once, at the
 * moment of approving, and this is what is left once that moment has passed. Names come
 * from `people`, the same list every other name on this row comes from, never from the
 * metadata itself. Null when the metadata carries nobody, which is every message this
 * did not happen on and every decline.
 */
function notWrittenToSentence(
  rosterEntryIds: readonly string[],
  people: readonly Person[],
): string | null {
  if (rosterEntryIds.length === 0) {
    return null;
  }
  const names = rosterEntryIds.map((id) => personFor(id, people)?.name ?? 'someone');
  // No actor, for the same reason the confirmation's note has none: say what did not
  // reach whom, rather than inventing someone who did not do it.
  return `This didn’t reach ${nameList(names)}. There’s no address for them yet.`;
}

function joinedSentence(subject: string | null, people: readonly Person[]): string {
  const person = personFor(subject, people);
  if (person === null) {
    return 'Someone new joined the conversation.';
  }
  return person.self
    ? 'You joined the conversation.'
    : `${person.name} joined the conversation.`;
}
